import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/elements/button';
import { Search, Download, Check } from '../../assets/icons';
import '../../assets/css/admin/labs.css';
import { labApi } from '../../network/apis/lab';
import { adminApi } from '../../network/apis/admin';
import LoadingSpinner from '../../components/elements/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminLab = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        status: 'all',
        type: 'all',
        location: 'all'
    });

    const [labs, setLabs] = useState([]);

    useEffect(() => {
        fetchLabs();
    }, []);

    const fetchLabs = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await labApi.getLabs();
            if (response?.results) {
                setLabs(response.results);
            } else {
                setLabs([]);
            }
        } catch (err) {
            console.error('Failed to fetch labs:', err);
            setError('Failed to load laboratories');
            setLabs([]);
        } finally {
            setLoading(false);
        }
    };

    // Calculate stats dynamically from fetched labs
    const stats = useMemo(() => {
        const totalLabs = labs.length;
        const activeLabs = labs.filter(lab => lab.is_verified === true).length;
        const pendingApprovals = labs.filter(lab => lab.is_verified === false).length;
        const suspendedLabs = 0; // Add if you have a suspended field
        return { totalLabs, activeLabs, pendingApprovals, suspendedLabs };
    }, [labs]);

    // Filter labs
    const filteredLabs = useMemo(() => {
        return labs.filter(lab => {
            const searchMatch = 
                lab.lab_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                lab.city?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const statusMatch = filters.status === 'all' || 
                (filters.status === 'active' && lab.is_verified) ||
                (filters.status === 'pending' && !lab.is_verified);
            
            const typeMatch = filters.type === 'all' || 
                lab.lab_type?.toLowerCase().includes(filters.type);
            
            const locationMatch = filters.location === 'all' || 
                lab.city?.toLowerCase().includes(filters.location);
            
            return searchMatch && statusMatch && typeMatch && locationMatch;
        });
    }, [labs, searchTerm, filters]);

    if (loading) {
        return (
            <div className="admin-labs">
                <div className="loading-state">
                    <LoadingSpinner />
                    <p>Loading laboratories...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-labs">
                <div className="error-state">
                    <p>{error}</p>
                    <Button text="Retry" onClick={fetchLabs} />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-labs">
            <div className="page-header">
                <div className="header-content">
                    <h1>Laboratory Management</h1>
                    <p>Monitor and manage registered laboratories</p>
                </div>
                <div className="header-actions">
                    <Button
                        icon={<img src={Download} alt="" />}
                        text="Export Report"
                        variant="outline"
                    />
                    <Link to="/admin/labs/applications">
                        <Button
                            icon={<img src={Check} alt="" />}
                            text="Review Applications"
                            variant="primary"
                        />
                    </Link>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <h3>Total Labs</h3>
                    <div className="stat-value">{stats.totalLabs}</div>
                </div>
                <div className="stat-card">
                    <h3>Active Labs</h3>
                    <div className="stat-value success">{stats.activeLabs}</div>
                </div>
                <div className="stat-card">
                    <h3>Pending Approvals</h3>
                    <div className="stat-value warning">{stats.pendingApprovals}</div>
                </div>
                <div className="stat-card">
                    <h3>Suspended</h3>
                    <div className="stat-value error">{stats.suspendedLabs}</div>
                </div>
            </div>

            <div className="filters-section">
                <div className="search-bar">
                    <img src={Search} alt="" />
                    <input
                        type="text"
                        placeholder="Search laboratories..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filters">
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="suspended">Suspended</option>
                    </select>

                    <select
                        value={filters.type}
                        onChange={(e) => setFilters({...filters, type: e.target.value})}
                    >
                        <option value="all">All Types</option>
                        <option value="full">Full Service</option>
                        <option value="specialized">Specialized</option>
                        <option value="research">Research</option>
                    </select>

                    <select
                        value={filters.location}
                        onChange={(e) => setFilters({...filters, location: e.target.value})}
                    >
                        <option value="all">All Locations</option>
                        <option value="lagos">Lagos</option>
                        <option value="abuja">Abuja</option>
                        <option value="ph">Port Harcourt</option>
                    </select>
                </div>
            </div>

            <div className="labs-grid">
                {filteredLabs.length === 0 ? (
                    <div className="empty-state">
                        <p>No laboratories found</p>
                    </div>
                ) : (
                    filteredLabs.map(lab => (
                    <div key={lab.id} className="lab-card">
                        <div className="card-header">
                            <div className="lab-info">
                                <h3>{lab.lab_name}</h3>
                                <span className={`${lab.is_verified ? 'active' : 'pending'}`}>
                                    {lab.is_verified ? 'Verified' : 'Pending'}
                                </span>
                            </div>
                            <div className="lab-meta">
                                <span className="type">{lab.lab_type}</span>
                                <span className="location">{lab.city}, {lab.state}</span>
                            </div>
                        </div>

                        <div className="lab-stats">
                            <div className="stat">
                                <label>Registration #</label>
                                <span>{lab.registration_number}</span>
                            </div>
                            <div className="stat">
                                <label>Established</label>
                                <span>{lab.established_date ? new Date(lab.established_date).getFullYear() : 'N/A'}</span>
                            </div>
                        </div>

                        <div className="lab-details">
                            {lab.director && (
                                <div className="director">
                                    <label>Lab Director</label>
                                    <span>{lab.director.name}</span>
                                </div>
                            )}
                            
                            {lab.certifications && lab.certifications.length > 0 && (
                                <div className="certifications">
                                    <label>Certifications</label>
                                    <div className="tags">
                                        {lab.certifications.map((cert, index) => (
                                            <span key={index} className="tag">{cert.name}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {lab.specializations && lab.specializations.length > 0 && (
                                <div className="specializations">
                                    <label>Specializations</label>
                                    <div className="tags">
                                        {lab.specializations.map((spec, index) => (
                                            <span key={index} className="tag">{spec.name}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="contact">
                                <div className="contact-item">
                                    <label>Email</label>
                                    <span>{lab.email}</span>
                                </div>
                                <div className="contact-item">
                                    <label>Phone</label>
                                    <span>{lab.phone}</span>
                                </div>
                            </div>
                        </div>

                        <div className="card-actions">
                            <Link to={`/admin/labs/${lab.id}`}>
                                <Button
                                    text="View Details"
                                    variant="outline"
                                />
                            </Link>
                            {!lab.is_verified && (
                                <Button
                                    text="Approve"
                                    variant="primary"
                                    onClick={async () => {
                                        try {
                                            await adminApi.approveLab(lab.id);
                                            toast.success('Lab approved');
                                            fetchLabs();
                                        } catch {
                                            toast.error('Approval failed');
                                        }
                                    }}
                                />
                            )}
                            {lab.is_verified && (
                                <Button
                                    text="Suspend"
                                    variant="error"
                                    onClick={async () => {
                                        try {
                                            await labApi.updateLab(lab.id, { is_active: false });
                                            toast.success('Lab suspended');
                                            fetchLabs();
                                        } catch {
                                            toast.error('Suspend failed');
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )))}
            </div>
        </div>
    );
};

export default AdminLab;